import { useEffect, useState } from "react";
import { isEmbeddedFrame } from "../isEmbeddedFrame";

export interface ParentViewport {
  /** Górna krawędź widocznej części strony klienta, w pikselach od góry iframe. */
  top: number;
  /** Dolna krawędź widocznej części strony klienta, w pikselach od góry iframe. */
  bottom: number;
  frameHeight: number;
}

const SETTLE_CHECK_MS = 160;
const LIVE_HOST_INTERVAL_MS = 100;
const LIVE_HOST_SAMPLES = 3;
const MOVE_TOLERANCE_PX = 1;

const parseScrollInfo = (data: unknown): ParentViewport | null => {
  if (typeof data !== "string" || !data.startsWith("parentScrollInfo::")) return null;

  const [, scroll, viewportHeight, frameTop, frameBottom] = data.split("::").map(parseFloat);
  if ([scroll, viewportHeight, frameTop, frameBottom].some((value) => value === undefined || isNaN(value))) {
    return null;
  }

  const top = scroll - frameTop;
  return { top, bottom: top + viewportHeight, frameHeight: frameBottom - frameTop };
};

const isSameViewport = (a: ParentViewport, b: ParentViewport) =>
  Math.abs(a.top - b.top) <= MOVE_TOLERANCE_PX &&
  Math.abs(a.bottom - b.bottom) <= MOVE_TOLERANCE_PX &&
  Math.abs(a.frameHeight - b.frameHeight) <= MOVE_TOLERANCE_PX;

const requestParentViewport = () => window.parent.postMessage("updateScrollPosition::", "*");

/**
 * Widoczna część strony klienta w współrzędnych iframe (parentScrollInfo:: ze skryptu klienta).
 *
 * Skrypt klienta w wersji v4 wysyła pozycję najwyżej co 250 ms i nie wysyła jej po zatrzymaniu
 * przewijania, więc po każdym ruchu dopytujemy sami, aż pozycja przestanie się zmieniać.
 * `isMoving`: strona klienta jest właśnie przewijana.
 * `isLive`: skrypt klienta wysyła pozycję na bieżąco i można za nią płynnie podążać.
 */
export default function useParentViewport() {
  const [viewport, setViewport] = useState<ParentViewport | null>(null);
  const [isMoving, setIsMoving] = useState(false);
  const [isLive, setIsLive] = useState(false);

  useEffect(() => {
    if (!isEmbeddedFrame()) return;

    let last: ParentViewport | null = null;
    let lastMoveAt = 0;
    let liveSamples = 0;
    let settleTimer: number | undefined;

    const handleMessage = (event: MessageEvent) => {
      const next = parseScrollInfo(event.data);
      if (!next) return;

      const hasMoved = last !== null && !isSameViewport(last, next);
      if (last === null || hasMoved) {
        setViewport(next);
      }
      last = next;

      window.clearTimeout(settleTimer);
      if (!hasMoved) {
        setIsMoving(false);
        return;
      }

      const now = Date.now();
      if (now - lastMoveAt < LIVE_HOST_INTERVAL_MS) {
        liveSamples += 1;
        if (liveSamples >= LIVE_HOST_SAMPLES) setIsLive(true);
      }
      lastMoveAt = now;

      setIsMoving(true);
      settleTimer = window.setTimeout(requestParentViewport, SETTLE_CHECK_MS);
    };

    window.addEventListener("message", handleMessage);
    requestParentViewport();

    return () => {
      window.removeEventListener("message", handleMessage);
      window.clearTimeout(settleTimer);
    };
  }, []);

  return { viewport, isMoving, isLive };
}
